import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { RefreshCw, Terminal, X } from 'lucide-react';
import { customAdminApi } from '../../../services/api';
import type { CustomLogEntry } from '../../../types/custom';
import { useSocket } from '../../../contexts/SocketContext';
import { customErrorMessage } from '../../../utils/customUi';

export interface CustomLogsTarget {
  id: string;
  name: string;
}

export function CustomLogsModal({ instance, onClose }: { instance: CustomLogsTarget; onClose: () => void }) {
  const { socket, connected } = useSocket();
  const [entries, setEntries] = useState<CustomLogEntry[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [snapshotLoaded, setSnapshotLoaded] = useState(false);
  const [following, setFollowing] = useState(false);
  const [error, setError] = useState('');
  const output = useRef<HTMLDivElement>(null);
  const cursorRef = useRef<string | null>(null);
  const overlay = useRef<HTMLDivElement>(null);
  const dialog = useRef<HTMLDivElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  useEffect(() => {
    const overlayElement = overlay.current;
    const dialogElement = dialog.current;
    if (!overlayElement || !dialogElement) return;

    const previousFocus = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    const previousBodyOverflow = document.body.style.overflow;
    const background = Array.from(document.body.children)
      .filter((element): element is HTMLElement => element instanceof HTMLElement && element !== overlayElement)
      .map(element => ({
        element,
        inert: element.inert,
        ariaHidden: element.getAttribute('aria-hidden'),
      }));
    for (const item of background) {
      item.element.inert = true;
      item.element.setAttribute('aria-hidden', 'true');
    }
    document.body.style.overflow = 'hidden';

    const focusFrame = requestAnimationFrame(() => closeButton.current?.focus());
    const keydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(dialogElement.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ));
      if (focusable.length === 0) {
        event.preventDefault();
        dialogElement.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', keydown);
    return () => {
      cancelAnimationFrame(focusFrame);
      document.removeEventListener('keydown', keydown);
      document.body.style.overflow = previousBodyOverflow;
      for (const item of background) {
        item.element.inert = item.inert;
        if (item.ariaHidden === null) item.element.removeAttribute('aria-hidden');
        else item.element.setAttribute('aria-hidden', item.ariaHidden);
      }
      previousFocus?.focus();
    };
  }, []);

  const merge = (incoming: CustomLogEntry[]) => {
    setEntries(current => {
      const seen = new Set(current.map(entry => entry.id));
      const merged = [...current];
      for (const entry of incoming) {
        if (!seen.has(entry.id)) {
          seen.add(entry.id);
          merged.push(entry);
        }
      }
      return merged.slice(-2_000);
    });
  };

  const loadSnapshot = useCallback(async () => {
    setError('');
    setSnapshotLoaded(false);
    try {
      const snapshot = await customAdminApi.getInstanceLogs(instance.id, 500);
      setEntries(snapshot.entries || []);
      cursorRef.current = snapshot.cursor;
      setCursor(snapshot.cursor);
      setSnapshotLoaded(true);
    } catch (caught) {
      setError(customErrorMessage(caught, 'The custom instance logs could not be loaded.'));
    }
  }, [instance.id]);

  useEffect(() => { void loadSnapshot(); }, [loadSnapshot]);

  useEffect(() => {
    if (!socket || !connected || !snapshotLoaded) return;
    let disposed = false;
    const ready = (payload: { instanceId?: string; cursor?: string }) => {
      if (!disposed && payload.instanceId === instance.id) {
        setFollowing(true);
        if (payload.cursor) {
          cursorRef.current = payload.cursor;
          setCursor(payload.cursor);
        }
      }
    };
    const data = (payload: { instanceId?: string; entries?: CustomLogEntry[]; data?: string }) => {
      if (disposed || payload.instanceId !== instance.id) return;
      if (Array.isArray(payload.entries)) merge(payload.entries);
      else if (typeof payload.data === 'string' && payload.data.length) {
        const now = new Date().toISOString();
        merge(payload.data.split(/\r?\n/).filter(Boolean).map((message, index) => ({
          id: `fallback:${now}:${index}:${message}`,
          stream: 'stdout',
          timestamp: now,
          message,
        })));
      }
    };
    const failed = (payload: { instanceId?: string; error?: string }) => {
      if (!disposed && payload.instanceId === instance.id) {
        setFollowing(false);
        setError(payload.error || 'Live log follow failed.');
      }
    };
    const ended = (payload: { instanceId?: string }) => {
      if (!disposed && payload.instanceId === instance.id) setFollowing(false);
    };
    socket.on('custom:logs:ready', ready);
    socket.on('custom:logs:data', data);
    socket.on('custom:logs:error', failed);
    socket.on('custom:logs:ended', ended);
    const since = cursorRef.current;
    socket.emit('custom:logs:start', { instanceId: instance.id, ...(since ? { since } : {}) });
    return () => {
      disposed = true;
      socket.emit('custom:logs:stop', { instanceId: instance.id });
      socket.off('custom:logs:ready', ready);
      socket.off('custom:logs:data', data);
      socket.off('custom:logs:error', failed);
      socket.off('custom:logs:ended', ended);
      setFollowing(false);
    };
  }, [connected, instance.id, snapshotLoaded, socket]);

  useEffect(() => {
    const element = output.current;
    if (element) element.scrollTop = element.scrollHeight;
  }, [entries]);

  const rendered = useMemo(() => entries.map((entry, index) => <div key={`${entry.id}:${index}`} className={entry.stream === 'stderr' ? 'text-red-300' : 'text-gray-300'}><span className="text-gray-600 select-none">{entry.timestamp ? new Date(entry.timestamp).toLocaleTimeString() : '--:--:--'} {entry.stream === 'stderr' ? 'ERR' : 'OUT'} </span>{entry.message}</div>), [entries]);

  return createPortal(<div ref={overlay} className="modal-overlay z-[100]" onClick={onClose}>
    <div ref={dialog} tabIndex={-1} className="modal-content max-w-5xl" role="dialog" aria-modal="true" aria-labelledby="custom-instance-logs-title" onClick={event => event.stopPropagation()}>
      <div className="modal-header"><div><h2 id="custom-instance-logs-title" className="modal-title flex items-center gap-2"><Terminal className="w-5 h-5 text-neon-400" /> {instance.name} logs</h2><p className="text-xs text-gray-500 mt-1">Snapshot-first recovery through Mothership, then admin-only live follow.</p></div><div className="flex items-center gap-2"><span className={following ? 'badge-online' : 'badge-offline'}>{following ? 'live' : (snapshotLoaded ? 'snapshot' : 'loading')}</span><button ref={closeButton} className="btn-ghost p-2" aria-label="Close logs" onClick={onClose}><X className="w-4 h-4" /></button></div></div>
      <div className="modal-body">
        {error && <div className="mb-3 p-3 rounded bg-red-500/10 border border-red-500/20 text-sm text-red-300 flex justify-between gap-3"><span>{error}</span><button className="text-red-200" aria-label="Retry log snapshot" onClick={() => void loadSnapshot()}><RefreshCw className="w-4 h-4" /></button></div>}
        <div ref={output} className="h-[60vh] overflow-auto rounded-lg bg-black/60 border border-dark-600 p-4 font-mono text-xs leading-5">{rendered.length ? rendered : <div className="text-gray-600">No log entries returned.</div>}</div>
      </div>
      <div className="modal-footer justify-between"><span className="text-xs text-gray-500">{entries.length} entries · cursor {cursor || 'not available'}</span><button className="btn-secondary" onClick={() => void loadSnapshot()}><RefreshCw className="w-4 h-4" /> Reload snapshot</button></div>
    </div>
  </div>, document.body);
}
